// External packages
import * as React from "react"
import { Paragraph, Box, Heading, Flex, Text } from "theme-ui"
import { keyframes } from "@emotion/react"
import { useLocation } from "@reach/router"
import { PricedProduct } from "@medusajs/medusa/dist/types/pricing"
import useDetectScroll from "@smakss/react-scroll-direction"

// Components
import Layout from "../components/layouts/layout"
import SEO from "../components/seo"
import {
  UiCategoryGrid,
  VisualItem,
  handlePageChange,
  getSelectedFiltersNumber,
  getFilterParam,
} from "./medusa/CategoryTemplate"
import {
  FilterDrawerContent,
  filterProducts,
} from "./medusa/AllProductsTemplate"
import { UiProgress } from "../components/v2/ui/Progress"
import { Button } from "../components/v2/Button"
import { Link } from "../components/v2/Link"
import { Picture } from "../components/v2/Picture"

// Contexts
import { useStore } from "../context/NewStoreContext"

// Utilities
import { getMinimumPrice } from "../components/v2/utils/product/getMinimumPrice"
import { getMaximumPrice } from "../components/v2/utils/product/getMaximumPrice"
import { generateGatsbyImageData } from "../shopstory/gatsby/imageUtils"

// Services
import { trackSearchProductClicked } from "../services/analytics"

// Hooks
import usePagination from "../hooks/v2/usePagination"
import {
  useSearch,
  useSearchNoResultsSuggestedProducts,
} from "../hooks/luigis-box/queries"
import { getDownCollectionTitle } from "../components/v2/utils/product/getTitle"
import { capitalize } from "lodash"
import { Product } from "@medusajs/medusa"
import { Icon } from "../components/v2/ui/Icon"
import useLockedBody from "../hooks/v2/useLockedBody"
import useLockedBodyMobile from "../hooks/v2/useLockedBodyMobile"
import { Drawer } from "../components/v2/ui/Drawer"
import { getBunnyImageSrc } from "../utils/bunny-cdn"
const CONTENT_PER_PAGE = 32

export const getSearchResultTitle = (p: PricedProduct) => {
  if (!p) {
    return ""
  }

  const category = p.categories?.filter((c) => c.parent_category_id)?.[0]

  const fabricLabel = category?.metadata?.fabric ?? ""
  const originalLabel = p.type?.value ?? ""

  if (!originalLabel) {
    return p.title
  }

  let title = fabricLabel
    ? `${fabricLabel} ${
        originalLabel.includes("shoes")
          ? originalLabel
          : originalLabel.charAt(0).toLowerCase() + originalLabel.slice(1)
      }`
    : originalLabel

  if (p.variants?.length) {
    if (
      p.variants.some(
        (v) => v.sku?.startsWith("TD-") || v.sku?.startsWith("DN-")
      )
    ) {
      const downCollection = p.variants.find(
        (v) => v.sku?.startsWith("TD-") || v.sku?.startsWith("DN-")
      )

      title = getDownCollectionTitle(downCollection.sku)
    }
  }

  return title
}

interface SelectedFilters {
  color: string[]
  fabric: string[]
  pattern: string[]
  category: string[]
}

const SORT_OPTIONS = [
  {
    label: "Latest arrivals",
    value: "latest-arrivals",
  },
  {
    label: "Price (Low to high)",
    value: "price-low-to-high",
  },
  {
    label: "Price (High to low)",
    value: "price-high-to-low",
  },
]

const SearchPage = ({ pageContext }) => {
  const { cart } = useStore()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const query = searchParams.get("q")
  const currentPage = searchParams.get("page")
    ? parseInt(searchParams.get("page"))
    : 1
  const [displayedProductsOptions, setDisplayedProductsOptions] =
    React.useState<{
      initialQuery: string
      products: PricedProduct[]
    }>({
      initialQuery: query,
      products: [],
    })
  const [tabId, setTabId] = React.useState(0)
  const [recentlyViewedProducts, setRecentlyViewedProducts] = React.useState([])
  const [totalHits, setTotalHits] = React.useState<number>(0)
  const [searchLimit, setSearchLimit] = React.useState(CONTENT_PER_PAGE)
  const { scrollDir, scrollPosition } = useDetectScroll()
  const categoryStickyBarRef = React.useRef(null)
  const { data: noResultsSuggestedProducts } =
    useSearchNoResultsSuggestedProducts(cart?.region_id, {
      enabled: Boolean(cart?.region_id),
      keepPreviousData: true,
    })

  const [selectedFilters, setSelectedFilters] =
    React.useState<SelectedFilters>("")
  const [selectedSort, setSelectedSort] = React.useState<string>()

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(false)
  const [locked, setLocked] = useLockedBody(false)
  const quickFiltersRef = React.useRef(null)
  const [accordionValues, setAccordionValues] = React.useState<string[]>([])
  const [lockedMobile, setLockedMobile] = useLockedBodyMobile(false)
  const filterFloatingButtonRef = React.useRef(null)
  const filterNavigationAnchorRef = React.useRef(null)

  const isFilterApplied = Object.values(selectedFilters).some((value) =>
    Boolean(value?.length)
  )

  const { data, isSuccess, isLoading, isRefetching } = useSearch(
    query,
    isFilterApplied ? 200 : CONTENT_PER_PAGE,
    currentPage,
    cart
  )

  React.useEffect(() => {
    setSelectedFilters({
      color: getFilterParam("color", searchParams),
      category: getFilterParam("category", searchParams),
      fabric: getFilterParam("fabric", searchParams),
      pattern: getFilterParam("pattern", searchParams),
    })
    setSelectedSort(searchParams.get("sort"))
  }, [location.search])

  React.useEffect(() => {
    if (query !== displayedProductsOptions.initialQuery) {
      setDisplayedProductsOptions({
        ...displayedProductsOptions,
        initialQuery: query,
        products: [],
      })
    }

    if (isLoading || isRefetching) {
      return
    }

    if (!data?.products?.length) {
      setDisplayedProductsOptions({
        ...displayedProductsOptions,
        products: [],
      })

      return
    }

    if (currentPage === 1) {
      setDisplayedProductsOptions({
        ...displayedProductsOptions,
        products: data.products,
      })
    } else {
      const displayedProductIds = displayedProductsOptions.products.map(
        (p) => p.id
      )
      const newProducts = data.products.filter(
        (p) => !displayedProductIds.includes(p.id)
      )

      setDisplayedProductsOptions({
        ...displayedProductsOptions,
        products: [...displayedProductsOptions.products, ...newProducts],
      })
    }
  }, [data, query])

  React.useEffect(() => {
    if (isSuccess && data?.total_hits) {
      setTotalHits(data.total_hits)

      if (data.total_hits === 0) {
        const recentlyViewed = JSON.parse(
          localStorage.getItem("tekla::recently_viewed")
        )

        if (recentlyViewed && recentlyViewed.length) {
          setRecentlyViewedProducts(recentlyViewed.slice(0, 4))
        }
      }
    }
  }, [isSuccess, data?.total_hits])

  const fadeIn = keyframes({
    from: { opacity: 0.2 },
    to: { opacity: 1 },
  })

  const filterData = pageContext.filter || []

  const sortProducts = (products: Product[]) => {
    if (!selectedSort) {
      return products
    }

    if (selectedSort === "latest-arrivals") {
      return products.sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      })
    }

    if (selectedSort === "price-low-to-high") {
      return products.sort((a, b) => {
        return a.priceMin - b.priceMin
      })
    }

    if (selectedSort === "price-high-to-low") {
      return products.sort((a, b) => {
        return b.priceMax - a.priceMax
      })
    }

    return products
  }

  const filtered = React.useMemo(
    () =>
      sortProducts(
        filterProducts(displayedProductsOptions?.products, selectedFilters)
      ),
    [displayedProductsOptions?.products, selectedFilters]
  )

  const totalNumOfProducts = isFilterApplied ? filtered?.length : totalHits
  const showing = isFilterApplied
    ? CONTENT_PER_PAGE * (currentPage - 1) + (totalNumOfProducts || 0)
    : CONTENT_PER_PAGE * (currentPage - 1) + (data?.products?.length || 0)

  const { nextPage, page, totalPages, prevPage } = usePagination({
    contentPerPage: CONTENT_PER_PAGE,
    count: totalNumOfProducts,
    currentPage: currentPage,
  })

  if (!query) {
    return (
      <Layout almostWhite>
        <SEO title={"Product Search"} />
        <Box sx={{ paddingBlockStart: [3, 21], paddingBlockEnd: [8, 18] }}>
          <Box sx={{ paddingInline: 4, marginBlockEnd: 3 }}>
            <Heading as="h1" sx={{ fontSize: "sm" }}>
              Please enter a search term
            </Heading>
          </Box>
        </Box>
      </Layout>
    )
  }

  return (
    <Layout almostWhite>
      <SEO title={"Product Search"} />
      <Box
        sx={{ paddingBlockStart: [3, 21], paddingBlockEnd: [8, 18] }}
        className={showing < 1 ? "luigisbox-no-results" : ""}
      >
        <Box sx={{ paddingInline: 4, marginBlockEnd: [0, 3] }}>
          <Heading as="h1" sx={{ fontSize: "md" }}>
            {showing > 0
              ? `Search results for “${query}”`
              : isSuccess && totalNumOfProducts === 0
              ? `No results for “${query}”`
              : null}
          </Heading>
        </Box>
        {page > 1 &&
          showing > 0 &&
          !isFilterApplied &&
          displayedProductsOptions?.products?.length < showing && (
            <Box sx={{ width: 53, textAlign: "center", marginInline: "auto" }}>
              <Button
                variant="ghost"
                href={`${location.pathname}?page=${page - 1}`}
                sx={{ marginBlockStart: 6 }}
                onClick={(e) => {
                  e.preventDefault()
                  prevPage()
                  handlePageChange(page - 1)
                }}
              >
                Show previous
              </Button>
            </Box>
          )}
        <Box ref={filterNavigationAnchorRef} />
        {showing > 0 || isFilterApplied ? (
          <Flex
            ref={categoryStickyBarRef}
            sx={{
              height: 13,
              backgroundColor: "grayscale.100",
              position: ["initial", "sticky"],
              top: "-1px",
              zIndex: "categoryBar",
              fontSize: "sm",
              alignItems: "center",
              justifyContent: "space-between",
              transform:
                scrollDir === "down"
                  ? "translateY(0)"
                  : scrollDir === "up" &&
                    scrollPosition.top >
                      categoryStickyBarRef?.current?.offsetTop
                  ? ["translateY(0)", "translateY(100%)"]
                  : null,
              transition: "transform .25s",
              paddingInline: 4,
              marginBlockEnd: 3,
            }}
          >
            <Text
              sx={{ color: "grayscale.600" }}
              className="luigisbox-showing-count"
            >
              {totalNumOfProducts} products
            </Text>
            <Box sx={{ display: ["none", "flex"], gap: 5 }}>
              <Button
                variant="link"
                iconLeft={
                  <Icon name="sliders" size="4" sx={{ marginBlockEnd: 1 }} />
                }
                onClick={() => {
                  setIsFilterDrawerOpen(true)
                  setLocked(true)
                }}
              >
                Filter
              </Button>
              <Box
                ref={quickFiltersRef}
                sx={{ display: ["none", "flex"], gap: 5 }}
              >
                {Object.keys(filterData)
                  .filter(
                    (value) =>
                      !(
                        Array.isArray(filterData[value]) &&
                        filterData[value].length < 2
                      )
                  )
                  .map((filterType) => (
                    <Button
                      key={filterType}
                      variant="link"
                      onClick={() => {
                        setAccordionValues([filterType])
                        setIsFilterDrawerOpen(true)
                      }}
                    >
                      {capitalize(filterType)}{" "}
                      {selectedFilters?.[filterType]?.length > 0
                        ? `(${selectedFilters?.[filterType]?.length})`
                        : filterType === "price" &&
                          selectedFilters["price_min"] !== null &&
                          selectedFilters["price_max"] !== null
                        ? "(1)"
                        : null}
                    </Button>
                  ))}
              </Box>
            </Box>
          </Flex>
        ) : null}
        {!isSuccess ? (
          <Flex sx={{ justifyContent: "center", marginBlockStart: 20 }}>
            <Flex
              className="luigisbox-loading-spinner"
              sx={{
                justifyContent: "center",
                div: {
                  width: 1,
                  height: 1,
                  borderRadius: "100%",
                  backgroundColor: "currentColor",
                  marginInline: 1,
                  marginBlockEnd: 10,
                  opacity: 0.2,
                },
              }}
            >
              <Box
                sx={{
                  animation: `${fadeIn} .6s linear infinite alternate`,
                }}
              />
              <Box
                sx={{
                  animation: `${fadeIn} .6s linear .15s infinite alternate`,
                }}
              />
              <Box
                sx={{
                  animation: `${fadeIn} .6s linear .30s infinite alternate`,
                }}
              />
            </Flex>
          </Flex>
        ) : null}
        {displayedProductsOptions?.products?.length > 0 ? (
          <UiCategoryGrid>
            {filtered?.map((item) => (
              <VisualItem
                key={item.id}
                title={getSearchResultTitle(item)}
                handle={`/product/${item.handle}`}
                image={generateGatsbyImageData({
                  imageUrl: item?.thumbnail?.startsWith("https:")
                    ? item.thumbnail
                    : `https:${item.thumbnail}`,
                })}
                priceMin={getMinimumPrice(item.variants)}
                priceMax={getMaximumPrice(item.variants)}
                onClick={() =>
                  trackSearchProductClicked({
                    query: query,
                    product: item,
                  })
                }
                wrapperClassName="luigisbox-search-result-item"
                titleClassName="luigisbox-search-result-item-title"
                priceClassName="luigisbox-search-result-item-price"
              />
            ))}
          </UiCategoryGrid>
        ) : null}
        <Box
          sx={{
            maxWidth: "100%",
            marginInline: "auto",
            marginBlockStart: showing > 0 && [13, 18],
          }}
        >
          {showing > 0 && displayedProductsOptions.products?.length ? (
            <Paragraph
              sx={{
                fontSize: "sm",
                textAlign: "center",
                marginBlockEnd: 6,
              }}
              className="luigisbox-showing-count-pagination"
            >
              {!isSuccess ? (
                <Flex sx={{ justifyContent: "center", marginBlockStart: 20 }}>
                  <Flex
                    sx={{
                      justifyContent: "center",
                      div: {
                        width: 1,
                        height: 1,
                        borderRadius: "100%",
                        backgroundColor: "currentColor",
                        marginInline: 1,
                        marginBlockEnd: 10,
                        opacity: 0.2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        animation: `${fadeIn} .6s linear infinite alternate`,
                      }}
                    />
                    <Box
                      sx={{
                        animation: `${fadeIn} .6s linear .15s infinite alternate`,
                      }}
                    />
                    <Box
                      sx={{
                        animation: `${fadeIn} .6s linear .30s infinite alternate`,
                      }}
                    />
                  </Flex>
                </Flex>
              ) : (
                `Showing ${showing} of ${totalNumOfProducts} products`
              )}
            </Paragraph>
          ) : null}
          {showing < 1 && isSuccess && totalNumOfProducts === 0 ? (
            <>
              <Flex
                sx={{
                  flexDirection: "column",
                  gap: 4,
                  fontSize: "sm",
                  paddingInline: 4,
                  marginBlockStart: 8,
                }}
              >
                <Paragraph>Explore:</Paragraph>
                <Paragraph>
                  <Link href="/all-products">All products</Link>
                </Paragraph>
                <Paragraph>
                  <Link href="/bestsellers">Bestsellers</Link>
                </Paragraph>
                <Paragraph>
                  <Link href="/new-in">New in</Link>
                </Paragraph>
              </Flex>
              <Box
                sx={{
                  backgroundColor: "grayscale.100",
                  paddingInline: 4,
                  paddingBlockStart: 10,
                }}
              >
                <Flex sx={{ gap: 5 }}>
                  {noResultsSuggestedProducts?.length ? (
                    <Link
                      onClick={() => setTabId(0)}
                      sx={{
                        fontSize: ["base", "md"],
                        color: tabId !== 0 ? "grayscale.600" : "primary",
                        transition: "color .2s",
                        marginBlockEnd: 6,
                      }}
                    >
                      Suggested products
                    </Link>
                  ) : null}
                  {recentlyViewedProducts?.length > 1 && (
                    <Link
                      onClick={() =>
                        setTabId(noResultsSuggestedProducts?.length ? 1 : 0)
                      }
                      sx={{
                        fontSize: ["base", "md"],
                        color: tabId === 1 ? "primary" : "grayscale.600",
                        transition: "color .2s",
                        marginBlockEnd: 6,
                      }}
                    >
                      Recently viewed
                    </Link>
                  )}
                </Flex>
                <Box>
                  <Box
                    sx={{
                      display: tabId === 0 ? "block" : "none",
                      overflowX: ["scroll", "unset"],
                      scrollbarWidth: "none",
                      marginInline: -4,

                      "::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {noResultsSuggestedProducts?.length ? (
                      <Flex
                        sx={{
                          gap: [4, 0],
                          overflowX: ["scroll", "unset"],
                          marginInline: [-2, 2],
                          scrollbarWidth: "none",

                          "::-webkit-scrollbar": {
                            display: "none",
                          },

                          "&::before, &::after": {
                            content: "''",
                            display: ["block", "none"],
                            flex: "0 0 8px",
                          },
                        }}
                      >
                        {noResultsSuggestedProducts.map((p) => {
                          const imageData = generateGatsbyImageData({
                            imageUrl: p?.thumbnail?.startsWith("https:")
                              ? p.thumbnail
                              : `https:${p.thumbnail}`,
                          })

                          return (
                            <Box
                              key={p.id}
                              sx={{
                                flexBasis: [
                                  "calc((((100% - 16px) - 16px) - 4px) / 1.5)",
                                  "25%",
                                ],
                                flexGrow: 0,
                                flexShrink: 0,
                                paddingInline: [0, 2],
                              }}
                            >
                              <Link
                                to={`/product/${p.handle}`}
                                sx={{ display: "block" }}
                              >
                                {imageData && (
                                  <Picture
                                    sources={imageData.images.sources}
                                    sx={{ height: "100%" }}
                                    imageProps={{
                                      width: 360,
                                      height: 450,
                                      sizes: imageData.images.fallback.sizes,
                                      src: getBunnyImageSrc(
                                        imageData.images.fallback.src
                                      ),
                                      srcSet: getBunnyImageSrc(
                                        imageData.images.fallback.srcSet
                                      ),
                                      alt: p.title,
                                    }}
                                  />
                                )}
                              </Link>
                              <Link
                                to={`/product/${p.handle}`}
                                sx={{ fontSize: "sm", marginBlockStart: 2 }}
                              >
                                {p.title}
                              </Link>
                            </Box>
                          )
                        })}
                      </Flex>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      display: tabId === 1 ? "block" : "none",
                      overflowX: ["scroll", "unset"],
                      scrollbarWidth: "none",
                      marginInline: [-4, 0],

                      "::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {recentlyViewedProducts?.length > 1 && (
                      <Flex
                        sx={{
                          gap: [4, 0],
                          overflowX: ["scroll", "unset"],
                          marginInline: -2,
                          scrollbarWidth: "none",

                          "::-webkit-scrollbar": {
                            display: "none",
                          },

                          "&::before, &::after": {
                            content: "''",
                            display: ["block", "none"],
                            flex: "0 0 8px",
                          },
                        }}
                      >
                        {recentlyViewedProducts?.map((p) => {
                          return (
                            <Box
                              key={p.id}
                              sx={{
                                flexBasis: [
                                  "calc((((100% - 16px) - 16px) - 4px) / 1.5)",
                                  "25%",
                                ],
                                flexGrow: 0,
                                flexShrink: 0,
                                paddingInline: [0, 2],
                              }}
                            >
                              <Link to={p.url} sx={{ display: "block" }}>
                                {p.thumbnail?.gatsbyImageData && (
                                  <Picture
                                    sources={
                                      p.thumbnail.gatsbyImageData.images.sources
                                    }
                                    sx={{ height: "100%" }}
                                    imageProps={{
                                      width: p.thumbnail.gatsbyImageData.width,
                                      height:
                                        p.thumbnail.gatsbyImageData.height,
                                      sizes:
                                        p.thumbnail.gatsbyImageData.images
                                          .fallback.sizes,
                                      src: getBunnyImageSrc(
                                        p.thumbnail.gatsbyImageData.images
                                          .fallback.src
                                      ),
                                      srcSet: getBunnyImageSrc(
                                        p.thumbnail.gatsbyImageData.images
                                          .fallback.srcSet
                                      ),
                                      alt: p.title,
                                    }}
                                  />
                                )}
                              </Link>
                              <Link
                                to={p.url}
                                sx={{ fontSize: "sm", marginBlockStart: 2 }}
                              >
                                {p.title}
                              </Link>
                            </Box>
                          )
                        })}
                      </Flex>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          ) : null}
          <Box sx={{ width: 53, textAlign: "center", marginInline: "auto" }}>
            {showing > 0 && displayedProductsOptions.products?.length ? (
              <UiProgress value={(showing / totalNumOfProducts) * 100} />
            ) : null}
            {page !== totalPages &&
            showing > 0 &&
            displayedProductsOptions.products?.length ? (
              <Button
                variant="ghost"
                href={`${location.pathname}?page=${page + 1}`}
                sx={{ marginBlockStart: 6 }}
                onClick={(e) => {
                  e.preventDefault()
                  nextPage()
                  handlePageChange(page + 1)
                }}
                disabled={page === totalPages || isFilterApplied}
                isVisuallyDisabled={page === totalPages || isFilterApplied}
              >
                Show more
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: ["none", "block"] }}>
        <Drawer
          isOpened={isFilterDrawerOpen}
          size="xl"
          onCloseClick={() => {
            setIsFilterDrawerOpen(false)
            setLocked(false)
          }}
          onBackdropClick={() => {
            setIsFilterDrawerOpen(false)
            setLocked(false)
          }}
        >
          <FilterDrawerContent
            filterData={filterData}
            numOfProducts={10}
            accordionValues={accordionValues}
            setAccordionValues={setAccordionValues}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            selectedFilters={selectedFilters}
            setIsFilterDrawerOpen={setIsFilterDrawerOpen}
            setLocked={setLocked}
            products={displayedProductsOptions?.products}
            isUSA={cart?.region?.name === "United States"}
            filterNavigationAnchorRef={filterNavigationAnchorRef}
            isMobile={false}
            isSearchPage={true}
          />
        </Drawer>
      </Box>
      <Box sx={{ display: ["flex", "none"] }}>
        <Drawer
          isOpened={isFilterDrawerOpen}
          position="bottom"
          size="xl"
          onCloseClick={() => {
            setIsFilterDrawerOpen(false)
            setLockedMobile(false)
          }}
          onBackdropClick={() => {
            setIsFilterDrawerOpen(false)
            setLockedMobile(false)
          }}
          onSwipeRight={() => {
            setIsFilterDrawerOpen(false)
            setLockedMobile(false)
          }}
        >
          <FilterDrawerContent
            filterData={filterData}
            numOfProducts={10}
            accordionValues={accordionValues}
            setAccordionValues={setAccordionValues}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            selectedFilters={selectedFilters}
            setIsFilterDrawerOpen={setIsFilterDrawerOpen}
            setLockedMobile={setLockedMobile}
            products={displayedProductsOptions?.products}
            isUSA={cart?.region?.name === "United States"}
            filterNavigationAnchorRef={filterNavigationAnchorRef}
            isMobile={true}
            isSearchPage={true}
          />
        </Drawer>
      </Box>
      <Box
        ref={filterFloatingButtonRef}
        sx={{
          transition: "opacity 0.2s, visibility 0.2s",
          display: ["block", "none"],
        }}
      >
        <Button
          variant="secondary"
          iconLeft={<Icon name="sliders" size="4" sx={{ marginBlockEnd: 1 }} />}
          onClick={() => {
            setIsFilterDrawerOpen(true)
            setLockedMobile(true)
          }}
          sx={{
            position: "fixed",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          Filter
          {getSelectedFiltersNumber(selectedFilters) > 0 &&
            " (" + getSelectedFiltersNumber(selectedFilters) + ")"}
        </Button>
      </Box>
    </Layout>
  )
}

export default SearchPage
